import React, { useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import { Icon } from '#components/Icon'
import { Link } from '#components/Link'
import { RoleGuard } from '#components/RoleGuard'
import { ExpandableNavItem } from '#components/SideNav/components/ExpandableNavItem'
import { NavigationItem } from '#components/SideNav/components/NavigationItem'
import { NavigationSection } from '#components/SideNav/components/NavigationSection'
import { useAuth } from '#hooks/useAuth'
import { useETicketsAvailability } from '#hooks/useETicketsAvailability'
import { useFetchCompanyStats } from '#hooks/useFetchCompanyStats'
import { useSidebar } from '#hooks/useSidebar'
import { isManagerOnly, ROLE_ADMIN, ROLE_SUPER_ADMIN } from '#interfaces/auth'
import { E_TICKET_PRODUCT_KEYS } from '#interfaces/contract'
import { useCompanyContract } from '#providers/CompanyContractProvider'

import type { IProps } from './interfaces'
import styles from './styles.module.scss'

export const CompanySideNav = ({ companyId, companyName, showBackLink = true }: IProps): React.JSX.Element => {
  const { t } = useTranslation()
  const { isCollapsed } = useSidebar()
  const { user } = useAuth()
  const { isProductEnabled } = useCompanyContract()
  const { stats: companyStats } = useFetchCompanyStats(companyId)

  const base = `/companies/${companyId}`
  const managerOnly = isManagerOnly(user?.roles)

  const hasPersonalCards = isProductEnabled('personal_cards')
  const hasSharedCards = isProductEnabled('shared_cards')
  const hasDayCards = isProductEnabled('day_cards')

  // E-tickets are only shown once the config is actually ready: enabled in the
  // contract AND an active proposition with a Tranzer externalId exists. While
  // the availability check is pending we keep showing (no flicker); once it
  // settles — including on error — unconfigured means hidden (DEV-2801).
  const eticketsEnabled = E_TICKET_PRODUCT_KEYS.some((key) => isProductEnabled(key))
  const { configured: eticketsConfigured, isKnown: eticketsConfigKnown } = useETicketsAvailability(
    companyId,
    eticketsEnabled,
  )
  const hasETickets = eticketsEnabled && !(eticketsConfigKnown && !eticketsConfigured)

  const showCardsSection = useMemo(
    (): boolean => hasPersonalCards || hasSharedCards || hasDayCards || hasETickets,
    [hasPersonalCards, hasSharedCards, hasDayCards, hasETickets],
  )

  return (
    <div className={styles.companyNav} data-testid="company-side-nav">
      {!isCollapsed && (
        <>
          {showBackLink && !managerOnly && (
            <Link
              href="/companies"
              className={styles.backLink}
              withUnderline={false}
              ariaLabel={t('Back to companies')}
              data-testid="back-to-companies"
            >
              <Icon name="arrow-left" width="14" height="14" />
              <span>{t('Back to companies')}</span>
            </Link>
          )}

          <div className={styles.companyHeader}>
            <span className={styles.companyLabel}>{t('Company')}</span>
            <span className={styles.companyName}>{companyName}</span>
          </div>
        </>
      )}

      {!managerOnly && (
        <NavigationSection>
          <NavigationItem label={t('Dashboard')} location={base} icon="squares" exact />
        </NavigationSection>
      )}

      {showCardsSection && (
        <NavigationSection>
          {hasPersonalCards && (
            <NavigationItem
              label={t('Personal cards')}
              location={`${base}/personal-cards`}
              icon="credit-card"
              badge={
                companyStats && companyStats.subscriptionPending > 0
                  ? String(companyStats.subscriptionPending)
                  : undefined
              }
            />
          )}

          {hasETickets && <NavigationItem label={t('E-Tickets')} location={`${base}/e-tickets`} icon="ticket" />}

          {hasSharedCards && (
            <NavigationItem label={t('Shared cards')} location={`${base}/anonymous-cards`} icon="credit-card" />
          )}

          {hasDayCards && <NavigationItem label={t('Day tickets')} location={`${base}/day-cards`} icon="ticket" />}
        </NavigationSection>
      )}

      {!managerOnly && (
        <NavigationSection title={t('Management')}>
          <RoleGuard requiredRole={ROLE_ADMIN}>
            <ExpandableNavItem label={t('Invoicing')} icon="file-text" storageKey="company-invoicing">
              {/* `exact` so /invoicing doesn't fuzzy-match /invoicing/lines
                  and light both items at once — the child route lives
                  on the URL prefix but is semantically a sibling, not
                  a sub-view. */}
              <NavigationItem label={t('Invoices')} location={`${base}/invoicing`} isChild exact />
              {/* Invoice lines is the InvoicingMS surface for manual /
                  portal-cost edits — financial settlement, not
                  day-to-day admin. Gate at the nav level so non-super-
                  admins don't see a link to a page they'd bounce off
                  anyway (the page-level RoleGuard already enforces it). */}
              <RoleGuard requiredRole={ROLE_SUPER_ADMIN}>
                <NavigationItem label={t('Invoice lines')} location={`${base}/invoicing/lines`} isChild />
              </RoleGuard>
            </ExpandableNavItem>
          </RoleGuard>
          <NavigationItem label={t('Admin users')} location={`${base}/admins`} icon="users" />

          <RoleGuard requiredRole={ROLE_SUPER_ADMIN}>
            <ExpandableNavItem label={t('Account')} icon="menu" storageKey="company-account">
              <NavigationItem label={t('Services and prices')} location={`${base}/propositions`} isChild />
              <NavigationItem label={t('Departments')} location={`${base}/departments`} isChild />
              <NavigationItem label={t('Cost centers')} location={`${base}/cost-centers`} isChild />
              <NavigationItem label={t('Project codes')} location={`${base}/project-codes`} isChild />
              <NavigationItem label={t('Custom fields')} location={`${base}/custom-fields`} isChild />
              <NavigationItem label={t('Distribution points')} location={`${base}/distribution-points`} isChild />
              <NavigationItem label={t('Repricing')} location={`${base}/repricing`} isChild />
              <NavigationItem label={t('Settings')} location={`${base}/settings`} isChild />
            </ExpandableNavItem>
          </RoleGuard>
        </NavigationSection>
      )}
    </div>
  )
}
